export type ConnectionHelpMethod = 'oauth2' | 'api_key';

export interface ConnectionHelp {
  readonly platform: string;
  readonly method: ConnectionHelpMethod;
  readonly before: readonly string[];
  readonly steps: readonly string[];
  readonly troubleshooting: readonly string[];
  readonly verificationNote?: string;
  readonly docs: readonly {label: string; url: string}[];
}

// Presentation copy for maintained provider references. This never selects an
// authorization flow, reads credentials or changes provider/account configuration.
const guides: Readonly<Record<string, Partial<Record<ConnectionHelpMethod, ConnectionHelp>>>> = {
  'performance.google_ads.oauth': {
    oauth2: {
      platform: 'Google Ads', method: 'oauth2',
      before: [
        'Use a Google work login that can already open the intended advertiser or its manager account in Google Ads.',
        'A Shine administrator must have completed the Google application setup for real advertising accounts.',
      ],
      steps: [
        'Choose the Google login with advertising access, even if it differs from the work login you normally use.',
        'Complete Google’s identity check and permission screen, then return to Shine.',
      ],
      troubleshooting: [
        'No accounts listed? Check the same login in Google Ads and confirm its manager-account access before reconnecting.',
        'Google blocks the app or says access is limited? Ask a Shine administrator to check the application approval and permitted test users.',
      ],
      docs: [{label: 'Google Ads access guide', url: 'https://developers.google.com/google-ads/api/docs/oauth/overview'}],
    },
  },
  'performance.meta_ads.oauth': {
    oauth2: {
      platform: 'Meta Ads', method: 'oauth2',
      before: [
        'Use this option for a customer business portfolio that is separate from the business owning the Shine app.',
        'Use a Facebook login with access to the intended business portfolio and advertising account.',
        'A Shine administrator must confirm that the Meta app is approved for customer-business access.',
      ],
      steps: [
        'In Facebook Login for Business, choose the intended customer business and advertising assets.',
        'Complete the reporting permission screen and any business approval, then return to Shine.',
      ],
      troubleshooting: [
        'Connecting the app-owning business itself? Create a separate Meta Ads connection with API key and a dedicated company System User token.',
        'A success message in Meta is only the sign-in step. Use Sources to confirm that Shine can access the selected advertiser.',
      ],
      docs: [{label: 'Meta business login guide', url: 'https://developers.facebook.com/documentation/facebook-login/facebook-login-for-business'}],
    },
    api_key: {
      platform: 'Meta Ads', method: 'api_key',
      before: [
        'Use this option for advertisers in the business that owns the Shine app. Ask its business administrator to prepare a dedicated Employee System User.',
        'Assign only the intended ad account’s View performance access and partial Develop app access to the Shine app. Personal user tokens are rejected.',
      ],
      steps: [
        'In Meta Business Settings → System users, select the dedicated user and Generate token for the Shine app.',
        'Choose 60 days (recommended) and only ads_read. If Meta requests approval from another business administrator, wait for that approval.',
        'Enter the generated token directly in Key value in Shine. Plan its replacement before it expires.',
      ],
      troubleshooting: [
        'Token approval pending? The business approver must complete it in Meta before the connection can be finished.',
        'Token rejected? Check the app, dedicated System User, ads_read permission, expiry and intended ad-account assignment with your administrator.',
        'An existing connection cannot switch between OAuth and API key. Create a new connection when changing this method.',
      ],
      docs: [{label: 'Meta System User token guide', url: 'https://developers.facebook.com/docs/business-management-apis/system-users/install-apps-and-generate-tokens'}],
    },
  },
  'performance.linkedin_ads.oauth': {
    oauth2: {
      platform: 'LinkedIn Ads', method: 'oauth2',
      before: [
        'Use the LinkedIn member login that has reporting access to the intended advertising account in Campaign Manager.',
        'A Shine administrator must have Advertising API approval for the Shine application.',
      ],
      steps: [
        'Sign in with the LinkedIn member who can view the advertising account.',
        'Approve the account and reporting permissions requested by Shine, then return to Connections.',
      ],
      troubleshooting: [
        'Company Page access alone does not establish ad-account access. Ask the Campaign Manager administrator to check your advertising-account role.',
        'Missing reporting permission or app approval? Ask a Shine administrator to check the Advertising API setup before retrying.',
      ],
      docs: [{label: 'LinkedIn advertising access guide', url: 'https://learn.microsoft.com/en-us/linkedin/marketing/increasing-access'}],
    },
  },
  'performance.dv360.oauth': {
    oauth2: {
      platform: 'DV360', method: 'oauth2',
      before: [
        'Use a Google work login with access to the intended DV360 partner or advertiser and its reporting.',
        'A Shine administrator must enable both Display & Video 360 and Bid Manager for the Google application.',
      ],
      steps: [
        'Choose the Google login that can open the intended advertiser in DV360. Google Ads access alone does not establish DV360 access.',
        'Complete the permissions for Display & Video 360 and Bid Manager, then return to Shine.',
      ],
      troubleshooting: [
        'An advertiser is missing? Check the same Google login and its partner/advertiser access in DV360.',
        'Reporting access fails? Ask your administrator to check Bid Manager access as well as the DV360 advertiser role.',
      ],
      verificationNote: 'DV360’s account check confirms advertiser visibility and access to report definitions. A generated advertiser report is checked separately when collection is set up.',
      docs: [{label: 'DV360 authorization guide', url: 'https://developers.google.com/display-video/api/guides/quickstart/configure-client'}],
    },
  },
  'performance.snapchat_ads.oauth': {
    oauth2: {
      platform: 'Snapchat Ads', method: 'oauth2',
      before: [
        'Use a Snapchat login assigned to the intended business and ad account, with Data Analyst or higher access.',
        'A Shine administrator must have configured the business-owned Snapchat advertising application.',
      ],
      steps: [
        'Sign in with the Snapchat login that can access the intended advertiser in Ads Manager.',
        'Complete Snapchat’s permission screen, then return to Shine and choose the intended accounts in Sources.',
      ],
      troubleshooting: [
        'A public profile or business membership alone is not an ad-account assignment. Ask the business administrator to check the advertiser access.',
        'No intended advertiser is available? Confirm the correct business before connecting another account.',
      ],
      docs: [{label: 'Snapchat business authorization guide', url: 'https://developers.snap.com/marketing-api/Ads-API/authentication'}],
    },
  },
  'performance.tiktok_ads.oauth': {
    oauth2: {
      platform: 'TikTok Ads', method: 'oauth2',
      before: [
        'Use a TikTok for Business login with access to the intended advertising account and its reports.',
        'A Shine administrator must complete developer review and application approval for advertiser and reporting access.',
      ],
      steps: [
        'Sign in with the TikTok for Business advertiser login and choose the advertising accounts you intend to connect.',
        'Complete the advertiser authorization and return to Shine. A creator login does not replace advertising-account access.',
      ],
      troubleshooting: [
        'Setup required? Ask a Shine administrator to finish the developer profile and application approval before connecting.',
        'Review still pending? Wait for TikTok’s decision; reconnecting or creating another app does not complete the review.',
      ],
      docs: [{label: 'TikTok advertiser authorization guide', url: 'https://business-api.tiktok.com/portal/docs?id=1738373141733378'}],
    },
  },
  'performance.chatgpt_ads.api_key': {
    api_key: {
      platform: 'ChatGPT Ads', method: 'api_key',
      before: [
        'You need a ChatGPT Ads advertiser account with Advertiser API access and its Ads API key. Each key connects one advertiser.',
        'Use the advertiser’s Ads API key, not a model API key or a conversion-tracking key.',
      ],
      steps: [
        'Obtain the advertiser’s Ads API key through your approved account setup or OpenAI partner contact.',
        'Enter the key directly in Key value, then save the connection.',
      ],
      troubleshooting: [
        'The wrong advertiser appears? The key belongs to that advertiser. Use the key for the account you intend to connect.',
        'Access is rejected or unavailable? Check the advertiser’s API access and key with the account administrator or OpenAI partner contact.',
      ],
      docs: [{label: 'ChatGPT Ads authentication guide', url: 'https://developers.openai.com/ads/api-reference/authentication'}],
    },
  },
};

export function connectionHelpFor(providerRef: string | undefined, authType: string | null | undefined): ConnectionHelp | null {
  if (!providerRef || (authType !== 'oauth2' && authType !== 'api_key')) return null;
  return guides[providerRef]?.[authType] ?? null;
}
